// Flight booking functions - Backend API integration
import { getVisitorId } from '@/hooks/useReferralCode';
import { searchRequestHeaders } from '@/lib/searchRequestHeaders';

/**
 * Validate a promo or discount code - server-side only. Never exposes valid
 * codes to the client, and never sends an amount: the server reads the discount
 * off the code row it looked up, so a tampered request cannot mint its own.
 *
 * @param {string} code - User-entered code
 * @param {string} cartId - Cart ID to apply it to
 * @param {{ viaLink?: boolean }} [opts] - viaLink marks a code that arrived from
 *   a creator's ?ref= link rather than being typed, which is recorded on the
 *   redemption so link-driven and recall-driven reach stay distinguishable.
 * @returns {Promise<{
 *   valid: boolean,
 *   error?: string,
 *   kind?: 'discount'|'service_fee_waiver',
 *   code?: string,
 *   discount_cents?: number,
 *   discount_label?: string
 * }>}
 */
export const validatePromoCode = async (code, cartId, opts = {}) => {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const res = await fetch('/api/promo/validate', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      code: (code || '').trim(),
      cart_id: cartId,
      via_link: !!opts.viaLink,
      // Ties this redemption to the click that produced it, so the creator
      // funnel is a join rather than two independent counters.
      visitor_id: getVisitorId(),
    })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { valid: false, error: data.error || 'Validation failed' };
  return { ...data, valid: !!data.valid };
};

/**
 * Remove promo from cart
 */
export const removePromoCode = async (cartId) => {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const res = await fetch('/api/promo/remove', {
    method: 'POST',
    headers,
    body: JSON.stringify({ cart_id: cartId })
  });
  const data = await res.json().catch(() => ({}));
  return res.ok && !!data.success;
};

export const initiateSlicePayFlow = async (paymentData) => {
  try {
    console.log('Initiating Slice Pay flow:', paymentData);
    
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    const response = await fetch('/api/slicepay/initiate', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Payment initiation failed');
    }

    return {
      data: data,
      status: response.status
    };
  } catch (error) {
    console.error('Slice Pay initiation error:', error);
    throw new Error('Payment initiation failed: ' + error.message);
  }
};

export const searchFlights = async (searchParams) => {
  try {
    console.log('Searching flights via backend API:', searchParams);
    
    // Call our backend API via Vite proxy
    console.log('Making API call to /api/flightsearch');
    const response = await fetch('/api/flightsearch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const backendResponse = await response.json();
    
    if (!backendResponse.success) {
      throw new Error(backendResponse.error || 'Flight search failed');
    }

    let flights = backendResponse.flights || [];
    
    // Apply client-side filters
    if (searchParams.maxPrice) {
      flights = flights.filter(flight => flight.price <= searchParams.maxPrice);
    }
    if (searchParams.airline) {
      flights = flights.filter(flight => 
        flight.slices?.some(slice => 
          slice.segments?.some(seg => 
            seg.marketing_carrier?.name?.toLowerCase().includes(searchParams.airline.toLowerCase())
          )
        )
      );
    }

    return {
      data: flights,
      status: 200
    };
  } catch (error) {
    console.error('Flight search failed:', error);
    return {
      data: { error: error.message },
      status: 500
    };
  }
};

// Client-side filters shared by both search calls below.
const filterSearchFlights = (flights, searchParams) => {
  let filtered = flights;
  if (searchParams.maxPrice) {
    filtered = filtered.filter(flight => parseFloat(flight.total_amount) <= searchParams.maxPrice);
  }
  if (searchParams.airline) {
    filtered = filtered.filter(flight =>
      flight.slices?.some(slice =>
        slice.segments?.some(seg =>
          seg.marketing_carrier?.name?.toLowerCase().includes(searchParams.airline.toLowerCase())
        )
      )
    );
  }
  return filtered;
};

// What a customer reads when a search (or a fare lookup, below) fails. The
// raw error stays in the console: it names upstream vendors and status
// codes, and on Vercel it was often just "HTTP 500: " (HTTP/2 replies carry
// no status text) — which is what the results page used to show.
const isOfflineError = (error) => (
  error instanceof TypeError && /fetch|network|load failed/i.test(error.message || '')
);
const OFFLINE_MESSAGE = 'We couldn\'t reach Defera. Check your connection and try again.';

// HTTP 429: too many searches from this connection in a short time (the
// flight-search budget, server/rate-limit-rules.js). "Try your search again"
// would only be refused again, so the message says how long to wait:
// Retry-After is the seconds until the budget refills. Without it (the iOS
// app can't read that header from another origin; a proxy's own 429) the
// wait is "a few minutes".
const rateLimitedError = (response) => {
  const error = new Error(`HTTP 429: ${response.statusText}`);
  error.rateLimited = true;
  const seconds = Number(response.headers?.get?.('retry-after'));
  error.retryAfterSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  return error;
};
const rateLimitedMessage = (seconds) => {
  let wait = 'a few minutes';
  if (seconds && seconds <= 60) wait = 'a minute';
  else if (seconds) wait = `about ${Math.ceil(seconds / 60)} minutes`;
  return `You've run a lot of searches in a short time. Please wait ${wait}, then search again.`;
};

const searchFailureMessage = (error) => {
  if (error?.rateLimited) return rateLimitedMessage(error.retryAfterSeconds);
  return isOfflineError(error)
    ? OFFLINE_MESSAGE
    : 'We couldn\'t load flights just now. Please try your search again.';
};
const searchFailureStatus = (error) => (error?.rateLimited ? 429 : 500);

// The search endpoint's one-body JSON reply → { data, status, meta }.
// Throws on any failure; callers turn that into { status: 500 }, or 429
// when the search was refused for too many searches.
const readSearchJson = async (response, searchParams) => {
  if (response.status === 429) throw rateLimitedError(response);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const backendResponse = await response.json();

  if (!backendResponse.success) {
    throw new Error(backendResponse.error || 'Defera Flights search failed');
  }

  const flights = filterSearchFlights(backendResponse.flights || [], searchParams);

  // `cheaper_alternatives` rides on the search response now (folded
  // in from SearchAPI's parallel call) — surface it on `meta` so
  // callers who care can consume it, without breaking existing
  // callers that only want the flights array.
  return {
    data: flights,
    status: 200,
    meta: {
      cheaperAlternatives: Array.isArray(backendResponse.cheaper_alternatives)
        ? backendResponse.cheaper_alternatives
        : [],
      sources: backendResponse.sources || null,
    },
  };
};

export const searchGoogleFlights = async (searchParams) => {
  try {
    console.log('Searching Defera Flights via SerpAPI:', searchParams);

    // Call our backend Defera Flights API endpoint
    console.log('Making API call to /api/defera-flights/search');
    const response = await fetch('/api/defera-flights/search', {
      method: 'POST',
      headers: searchRequestHeaders(),
      body: JSON.stringify(searchParams)
    });

    return await readSearchJson(response, searchParams);
  } catch (error) {
    console.error('Defera Flights search failed:', error);
    return {
      data: { error: searchFailureMessage(error) },
      status: searchFailureStatus(error)
    };
  }
};

// Reads the newline-delimited reply of a streamed search (see the
// /api/defera-flights/search comment in server.js for the message types).
// Flights that already arrived are kept if the stream breaks afterwards;
// a stream that breaks before any flights is a failed search.
const readSearchStream = async (response, searchParams, onFlights) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let flights = [];
  let gotFlights = false;
  let complete = false;
  let done = null;
  let streamError = null;

  const handleLine = (line) => {
    const message = JSON.parse(line);
    if (message.type === 'flights') {
      const batch = filterSearchFlights(Array.isArray(message.flights) ? message.flights : [], searchParams);
      flights = message.phase === 'google' ? batch : flights.concat(batch);
      gotFlights = true;
      complete = message.phase !== 'google';
      // `ms`: when the server sent this list, from when the request reached it.
      onFlights?.(flights, { complete, serverMs: Number.isFinite(message.ms) ? message.ms : null });
    } else if (message.type === 'done') {
      done = message;
    } else if (message.type === 'error') {
      streamError = message.error || 'Defera Flights search failed';
    }
  };

  try {
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });
      let newline = buffer.indexOf('\n');
      while (newline !== -1) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (line) handleLine(line);
        newline = buffer.indexOf('\n');
      }
      if (finished) break;
    }
    buffer += decoder.decode();
    if (buffer.trim()) handleLine(buffer.trim());
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    streamError = streamError || error?.message || 'Defera Flights search failed';
  }

  if (!gotFlights) throw new Error(streamError || 'Defera Flights search failed');
  return {
    data: flights,
    status: 200,
    meta: {
      cheaperAlternatives: Array.isArray(done?.cheaper_alternatives) ? done.cheaper_alternatives : [],
      sources: done?.sources || null,
      // True when the list stopped short of the server's final answer.
      partial: !complete || !!streamError,
    },
  };
};

/**
 * searchGoogleFlights for the results page: Google's list arrives first and
 * `onFlights(flightsSoFar, { complete })` fires with it, then again once
 * Duffel's extra flights are merged in (`complete: true`). Resolves with the
 * same { data, status, meta } shape as searchGoogleFlights. Errors before the
 * stream opens — and any server that doesn't stream — come back as the
 * one-body JSON reply and are read exactly as searchGoogleFlights reads them.
 * An abort (`signal`) rejects with the AbortError instead of resolving.
 */
export const streamGoogleFlights = async (searchParams, { onFlights, signal } = {}) => {
  try {
    const response = await fetch('/api/defera-flights/search', {
      method: 'POST',
      headers: searchRequestHeaders(),
      body: JSON.stringify({ ...searchParams, stream: true }),
      signal,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('application/x-ndjson') || !response.body) {
      return await readSearchJson(response, searchParams);
    }
    return await readSearchStream(response, searchParams, onFlights);
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('Defera Flights search failed:', error);
    return {
      data: { error: searchFailureMessage(error) },
      status: searchFailureStatus(error)
    };
  }
};

/** `parentSearchId`: the picked outbound's `_search_id`, so the search
 *  history chains the return list to its search. */
export const searchGoogleFlightsWithToken = async (departureToken, originalSearchParams, { parentSearchId = null } = {}) => {
  try {
    console.log('Fetching return flights with departure_token:', departureToken);
    
    // Call our backend Defera Flights API endpoint with departure_token
    const response = await fetch('/api/defera-flights/search-with-token', {
      method: 'POST',
      headers: searchRequestHeaders(),
      body: JSON.stringify({ 
        departure_token: departureToken,
        search_params: originalSearchParams,
        ...(parentSearchId ? { parent_search_id: parentSearchId } : {}),
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const backendResponse = await response.json();
    
    if (!backendResponse.success) {
      throw new Error(backendResponse.error || 'Return flights search failed');
    }

    return backendResponse.flights || [];
  } catch (error) {
    console.error('Return flights search failed:', error);
    throw error;
  }
};

// The fare page's toast when getGoogleFlightsBookingOptions throws. It used
// to print "Failed to load booking options: " plus the raw error ("HTTP
// 500: " in production). The page then sends the customer back to search,
// so the line says to search again.
export const fareOptionsFailureMessage = (error) => (
  isOfflineError(error)
    ? OFFLINE_MESSAGE
    : 'We couldn\'t load the fares for this flight. Please search again.'
);

/** `parentSearchId`: the picked flight's `_search_id`, so the search history
 *  chains the fares to the list they were picked from. */
export const getGoogleFlightsBookingOptions = async (bookingToken, searchParams, { parentSearchId = null } = {}) => {
  try {
    console.log('Fetching booking options with booking_token:', bookingToken);
    
    // Call our backend Defera Flights API endpoint with booking_token
    const response = await fetch('/api/flightoptions', {
      method: 'POST',
      headers: searchRequestHeaders(),
      body: JSON.stringify({ 
        booking_token: bookingToken,
        search_params: searchParams,
        ...(parentSearchId ? { parent_search_id: parentSearchId } : {}),
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const backendResponse = await response.json();
    
    if (!backendResponse.success) {
      throw new Error(backendResponse.error || 'Booking options fetch failed');
    }

    console.log('Booking options API response:', backendResponse);
    console.log('Booking options count:', backendResponse.booking_options?.length);
    if (backendResponse.booking_options?.length > 0) {
      const firstOption = backendResponse.booking_options[0];
      const mainOption = firstOption.together || firstOption.departing;
      console.log('First booking option price:', mainOption?.price);
    }

    return {
      bookingOptions: backendResponse.booking_options || [],
      selectedFlights: backendResponse.selected_flights || [],
      baggagePrices: backendResponse.baggage_prices || {},
      priceInsights: backendResponse.price_insights || null,
      // Provenance flag — 'duffel' when the booking options came from
      // /api/flightoptions' Duffel branch, otherwise undefined (SerpAPI /
      // Google Flights). FlightOptions.jsx uses this to skip the
      // refundable-upsell auto-hide, so a customer who picked a $198
      // Duffel return card doesn't get silently upsold to a $339
      // Refundable brand at the fare-selection step.
      source: backendResponse.source || null,
    };
  } catch (error) {
    console.error('Booking options fetch failed:', error);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    throw error;
  }
};

export const checkDeferaZeroRefundable = async (bookingToken, searchParams) => {
  const { deferaZeroUrl } = await import('@/lib/apiUrls');
  const response = await fetch(deferaZeroUrl('check-refundable'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ booking_token: bookingToken, search_params: searchParams })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Check failed');
  return data.has_refundable === true;
};

export const getOfferRequest = async (flightId) => {
  try {
    // Mock offer request
    await new Promise(resolve => setTimeout(resolve, 800)); // Simulate API delay
    
    return {
      success: true,
      offerId: Math.random().toString(36).substr(2, 9),
      flightId: flightId,
      validUntil: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
      terms: 'Standard booking terms apply',
      cancellationPolicy: 'Free cancellation within 24 hours',
      changePolicy: 'Changes allowed with fee'
    };
  } catch (error) {
    throw new Error('Offer request failed: ' + error.message);
  }
};

/**
 * Fetches Duffel seat maps for a live offer via the server proxy.
 * Returns { data, status } matching what SeatMap.jsx expects: on success,
 * data.data is Duffel's array of seat maps (one per segment).
 * Any non-200 status means "no seat map available" — callers should treat
 * that as a silent skip, never a blocking error.
 */
export const getSeatMaps = async ({ offer_id }) => {
  const response = await fetch(`/api/seat-maps?offer_id=${encodeURIComponent(offer_id || '')}`);
  const data = await response.json().catch(() => ({}));
  return { data, status: response.status };
};

/**
 * Finds Duffel seat maps for a SerpAPI/Google Flights selection by re-searching
 * Duffel for the same slice and matching on the flight-number chain.
 * Returns { data, status }; data.matched is false when the flight isn't
 * bookable on Duffel — callers silently skip seat selection in that case.
 */
export const findSeatMapsForFlight = async (payload) => {
  const response = await fetch('/api/seat-maps/find-for-flight', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  return { data, status: response.status };
};

// ============================================================================
// Hotel Search Functions - Defera Stays Integration
// ============================================================================

/**
 * Search for ALL hotels using Google Hotels API (fetches all pages automatically)
 * @param {Object} searchParams - Hotel search parameters
 * @param {string} searchParams.destination - Destination city or location
 * @param {string} searchParams.check_in_date - Check-in date (YYYY-MM-DD)
 * @param {string} searchParams.check_out_date - Check-out date (YYYY-MM-DD)
 * @param {number} searchParams.adults - Number of adults
 * @param {number} searchParams.children - Number of children
 * @returns {Promise<Object>} All hotel search results
 */
export const searchAllHotels = async (searchParams) => {
  try {
    console.log('Searching ALL hotels via backend API:', searchParams);
    
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    const response = await fetch('/api/google-hotels/search-all', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Hotel search failed');
    }

    console.log(`Loaded ${data.totalResults} hotels total`);

    return {
      data: data.data || [],
      status: response.status,
      totalResults: data.totalResults || 0
    };
  } catch (error) {
    console.error('Hotel search error:', error);
    throw new Error('Hotel search failed: ' + error.message);
  }
};

/**
 * Search for hotels using Duffel Stays API via backend
 * @param {Object} searchParams - Hotel search parameters
 * @param {number} searchParams.latitude - Latitude for location search
 * @param {number} searchParams.longitude - Longitude for location search
 * @param {number} searchParams.radius - Search radius in km (default: 5)
 * @param {string} searchParams.check_in_date - Check-in date (YYYY-MM-DD)
 * @param {string} searchParams.check_out_date - Check-out date (YYYY-MM-DD)
 * @param {number} searchParams.rooms - Number of rooms (default: 1)
 * @param {number} searchParams.adults - Number of adults (default: 2)
 * @param {number} searchParams.children - Number of children (default: 0)
 * @param {boolean} searchParams.free_cancellation_only - Only free cancellation rates (default: false)
 * @returns {Promise<Object>} Hotel search results
 */
export const searchHotels = async (searchParams) => {
  try {
    console.log('Searching hotels via Duffel API:', searchParams);
    
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    const response = await fetch('/api/duffel-stays/search', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Hotel search failed');
    }

    return {
      data: data.data || [],
      status: response.status,
      totalResults: data.totalResults || 0
    };
  } catch (error) {
    console.error('Hotel search error:', error);
    throw new Error('Hotel search failed: ' + error.message);
  }
};

/**
 * Fetch room rates for a specific hotel search result
 * @param {string} searchResultId - The search result ID (e.g., srr_xxxxx)
 * @returns {Promise<Object>} Complete hotel data with room types and rates
 */
export const fetchRoomRates = async (searchResultId) => {
  try {
    console.log('Fetching room rates for search result:', searchResultId);
    
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    const response = await fetch(`/api/duffel-stays/fetch-rates/${searchResultId}`, {
      method: 'POST',
      headers: headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch room rates');
    }

    return {
      data: data.data,
      status: response.status
    };
  } catch (error) {
    console.error('Fetch room rates error:', error);
    throw new Error('Failed to fetch room rates: ' + error.message);
  }
};

/**
 * Select and save a hotel with room selection to the stays table
 * @param {Object} hotel - Selected hotel data
 * @param {Object} selectedRate - Selected room rate
 * @param {String} sessionId - Session ID for abandoned trip tracking
 * @returns {Promise<Object>} Saved stay record
 */
export const selectHotel = async (hotel, selectedRate, sessionId = null) => {
  try {
    console.log('Saving hotel selection:', hotel.name, 'with rate:', selectedRate.id);
    console.log('Hotel data:', {
      name: hotel.name,
      check_in_date: hotel.check_in_date,
      check_out_date: hotel.check_out_date,
      nights: hotel.nights,
      search_result_id: hotel.search_result_id,
      id: hotel.id,
      latitude: hotel.latitude,
      longitude: hotel.longitude
    });
    console.log('Selected rate data:', {
      id: selectedRate.id,
      total_amount: selectedRate.total_amount,
      total_currency: selectedRate.total_currency,
      currency: selectedRate.currency
    });
    
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    const requestBody = { hotel, selectedRate, session_id: sessionId };
    console.log('Request payload:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch('/api/duffel-stays/select', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    const responseText = await response.text();
    console.log('Response status:', response.status);
    console.log('Response body:', responseText);

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { error: responseText || `HTTP ${response.status}: ${response.statusText}` };
      }
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = JSON.parse(responseText);
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to save hotel selection');
    }

    return {
      data: data.stay,
      status: response.status
    };
  } catch (error) {
    console.error('Hotel selection error:', error);
    console.error('Error stack:', error.stack);
    throw new Error('Failed to save hotel selection: ' + error.message);
  }
};

/**
 * Search Google Travel Explore destinations
 * @param {Object} searchParams - Search parameters for Google Travel Explore
 * @returns {Promise<Object>} Response with destinations or flights data
 */
export const searchGoogleTravelExplore = async (searchParams) => {
  try {
    console.log('Searching Google Travel Explore:', searchParams);
    
    const response = await fetch('/api/google-travel-explore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const backendResponse = await response.json();
    
    if (!backendResponse.success) {
      throw new Error(backendResponse.error || 'Travel explore search failed');
    }

    return {
      data: backendResponse.data,
      status: 200
    };
  } catch (error) {
    console.error('Google Travel Explore search failed:', error);
    return {
      data: { error: error.message },
      status: 500
    };
  }
};
